import DataPointSearchModal from '@components/_shared/DataPointSearch/DataPointSearchModal';
import FormattedAlias from '@components/_shared/Translations/FormattedAlias';
import QueryDropdowns from '@components/Home/Historian/QueryDropdowns';
import { Device } from '@src/types/Device';
import { SiteMeta } from '@src/types/SiteMeta';
import storeConnector from '@store/storeConnector';
import { HistorianDataPoint, SavedSearch, TimeMode } from '@types';
import React, { memo, ReactElement, useCallback, useState } from 'react';
import DataPointSearchButton from './AddByTopic/DataPointSearchButton';
import AddDataPointButton from './AddDataPointButton';
import { DATA_POINT_LENGTH_LIMIT } from './historianUtils';
import { useDataPointConverter } from './hooks/useDataPointConverter';
import { useHistorianSearch } from './hooks/useHistorianSearch';
import PlotButtons from './PlotButtons';
import SavedSearchRow from './SavedSearchRow/SavedSearchRow';
import SettingsRow from './SettingsRow';

const MemoQueryDropdowns = memo(QueryDropdowns);

interface FilterBlockProps extends FilterBlockContainerProps {
  activeSearch: SavedSearch;
  setActiveSearch: React.Dispatch<React.SetStateAction<SavedSearch>>;
  removeDataPoint: (i: number) => void;
  cloneDataPoint: (i: number, dp: HistorianDataPoint) => void;
  updateDataPoint: (i: number, dp: HistorianDataPoint) => void;
  searchUnderEdit: SavedSearch | undefined;
  setSearchUnderEdit: React.Dispatch<SavedSearch | undefined>;
  setSearchField: (field: string, value: unknown) => void;
  initialSearchState: SavedSearch;
}

function FilterBlock(p: FilterBlockProps): ReactElement {
  const [showDataPointSearchModal, setShowDataPointSearchModal] =
    useState(false);

  const { addDataPointsFromSearch } = useDataPointConverter(p.setActiveSearch);

  const isDataPointSearchEnabled =
    p.siteMeta.ui.features?.HistorianDatapointSearch_Enable ?? false;

  const openModal = useCallback(() => setShowDataPointSearchModal(true), []);
  const closeModal = useCallback(() => setShowDataPointSearchModal(false), []);

  return (
    <div className='filter-grid'>
      <div className='label no-pd-top'>
        <FormattedAlias id='Saved Search' />
      </div>
      <SavedSearchRow
        activeSearch={p.activeSearch}
        setSearchField={p.setSearchField}
        setActiveSearch={p.setActiveSearch}
        searchUnderEdit={p.searchUnderEdit}
        setSearchUnderEdit={p.setSearchUnderEdit}
        newSearch={p.initialSearchState}
      />
      <SettingsRow
        siteMeta={p.siteMeta}
        timeMode={p.timeMode}
        activeSearch={p.activeSearch}
        setSearchField={p.setSearchField}
      />
      {p.activeSearch.dataPoints.map((dp, i) => (
        <MemoQueryDropdowns
          // eslint-disable-next-line react/no-array-index-key
          key={`dataPoint_${dp.category}_${i}`}
          disabled={DATA_POINT_LENGTH_LIMIT <= p.activeSearch.dataPoints.length}
          cloneDataPoint={p.cloneDataPoint}
          removeDataPoint={p.removeDataPoint}
          updateDataPoint={p.updateDataPoint}
          dp={dp}
          i={i}
          categories={p.categories}
          units={p.units}
        />
      ))}
      <AddDataPointButton
        activeSearch={p.activeSearch}
        searchUnderEdit={p.searchUnderEdit}
        setActiveSearch={p.setActiveSearch}
      />
      {isDataPointSearchEnabled && (
        <DataPointSearchButton onClick={openModal} />
      )}
      <PlotButtons
        searchUnderEdit={p.searchUnderEdit}
        activeSearch={p.activeSearch}
        runPlot={p.runPlot}
        runAutoPlot={p.runAutoPlot}
      />
      {isDataPointSearchEnabled && (
        <DataPointSearchModal
          open={showDataPointSearchModal}
          onClose={closeModal}
          onAddDataPoints={addDataPointsFromSearch}
        />
      )}
    </div>
  );
}

interface FilterBlockContainerProps {
  siteMeta: SiteMeta;
  timeMode: TimeMode;
  runPlot: () => Promise<void>;
  runAutoPlot: (val: string) => void;
  categories: string[];
  units: Device[];
}

export function FilterBlockContainer(
  p: FilterBlockContainerProps,
): ReactElement {
  const { siteMeta, timeMode } = p;
  const { GPSLat, GPSLong, siteId, DataRate } = siteMeta;

  const {
    activeSearch,
    setActiveSearch,
    searchUnderEdit,
    setSearchUnderEdit,
    initialSearchState,
    cloneDataPoint,
    removeDataPoint,
    updateDataPoint,
    setSearchField,
  } = useHistorianSearch({ GPSLat, GPSLong, siteId, DataRate, timeMode });

  return (
    <FilterBlock
      {...p}
      activeSearch={activeSearch}
      setActiveSearch={setActiveSearch}
      cloneDataPoint={cloneDataPoint}
      removeDataPoint={removeDataPoint}
      updateDataPoint={updateDataPoint}
      searchUnderEdit={searchUnderEdit}
      setSearchUnderEdit={setSearchUnderEdit}
      setSearchField={setSearchField}
      initialSearchState={initialSearchState}
    />
  );
}

export default storeConnector(FilterBlockContainer, {
  config: ['siteMeta'],
  service: ['timeMode'],
});
